import * as THREE from 'three';
import { nearFade } from './fishBatch';

/** A sprite as drawn, for a tap: its middle, its size, scene units, and its fading (0 to 1). */
export interface Pickable {
  middle: THREE.Vector3;
  width: number;
  height: number;
  alpha: number;
}

/**
 * The sprite under a point on the screen (normalized device coordinates), within its box on the screen or `withinPx` of
 * its middle where the box is smaller, the nearest; null for none (M152, the honu's and the whale's rule: none half
 * faded, none faded out by its nearness to the camera, `nearFade`). Its box is read along the camera's own right and up,
 * as the sprites face it, so a habitat's turned camera reads it as drawn.
 */
export function pickNearest<T extends Pickable>(items: Iterable<T>, camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): T | null {
  camera.updateMatrixWorld();
  const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize();
  const [at, side, top] = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  let best = Infinity;
  let found: T | null = null;
  for (const item of items) {
    if (item.alpha < 0.5) continue;
    at.copy(item.middle).project(camera);
    if (at.z < -1 || at.z > 1) continue;
    side.copy(item.middle).addScaledVector(right, item.width / 2).project(camera);
    top.copy(item.middle).addScaledVector(up, item.height / 2).project(camera);
    const [halfW, halfH] = [(Math.abs(side.x - at.x) * widthPx) / 2, (Math.abs(top.y - at.y) * heightPx) / 2];
    if (nearFade(2 * halfW, widthPx) < 0.5) continue;
    const [dx, dy] = [(Math.abs(at.x - x) * widthPx) / 2, (Math.abs(at.y - y) * heightPx) / 2];
    if (dx <= Math.max(withinPx, halfW) && dy <= Math.max(withinPx, halfH) && Math.hypot(dx, dy) < best) {
      best = Math.hypot(dx, dy);
      found = item;
    }
  }
  return found;
}

/**
 * How near a tap is to a sprite as drawn: the tap's offset from its middle over its half-size on the screen (`withinPx`
 * at least), each way, combined; Infinity where the tap is off its box or it is not seen (M152: a tap on a seal beside
 * a whale's tail was the whale's, the whale tried first and its box its whole picture; of the animals under a tap, the
 * one whose middle is nearest, for its size, takes it).
 */
export function tapNearness(item: Pickable, camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
  camera.updateMatrixWorld();
  if (item.alpha < 0.5) return Infinity;
  const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize();
  const at = item.middle.clone().project(camera);
  if (at.z < -1 || at.z > 1) return Infinity;
  const side = item.middle.clone().addScaledVector(right, item.width / 2).project(camera);
  const top = item.middle.clone().addScaledVector(up, item.height / 2).project(camera);
  const [halfW, halfH] = [Math.max(withinPx, (Math.abs(side.x - at.x) * widthPx) / 2), Math.max(withinPx, (Math.abs(top.y - at.y) * heightPx) / 2)];
  const [dx, dy] = [(Math.abs(at.x - x) * widthPx) / 2, (Math.abs(at.y - y) * heightPx) / 2];
  return dx <= halfW && dy <= halfH ? Math.hypot(dx / halfW, dy / halfH) : Infinity;
}

/**
 * Whether a sprite is seen by `camera` (M161): shown more than half, its middle in front of the camera and within
 * `visible` of the screen (left, right, bottom, top, normalized device coordinates: the part the panels leave), and not
 * faded out by its nearness (`nearFade`, as a tap's).
 */
export function seenBy(item: Pickable, camera: THREE.Camera, visible: readonly [number, number, number, number] = [-1, 1, -1, 1]): boolean {
  if (item.alpha <= 0.5) return false;
  camera.updateMatrixWorld();
  const at = item.middle.clone().project(camera);
  if (at.z < -1 || at.z > 1 || at.x < visible[0] || at.x > visible[1] || at.y < visible[2] || at.y > visible[3]) return false;
  const side = item.middle.clone().addScaledVector(new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize(), item.width / 2).project(camera);
  // Its half width in the screen's coordinates, which run 2 across: its length a share of the screen's width.
  return nearFade(Math.abs(side.x - at.x), 1) >= 0.5;
}
